import type { FC } from "react"

import type { SupplementalAttemptDto } from "~/api/dto/contract-package.dto"

interface SupplementalPackageAlertsProps {
  attempts: SupplementalAttemptDto[]
  onRetrySend?: (attemptId: string) => void
  pendingAttemptId?: string | null
  actionError?: string | null
  actionErrorAttemptId?: string | null
}

const HEADINGS: Record<string, string> = {
  failed: `Supplemental documents were not sent`,
  blocked: `Supplemental documents could not be prepared`,
  send_unknown: `Supplemental documents may not have been sent`,
  claimed: `Supplemental documents did not finish sending`,
}

/**
 * States the seller must be told about even when nothing can be done from here,
 * such as a send whose outcome the provider left unknown.
 */
const ATTENTION_STATES = new Set([`failed`, `blocked`, `send_unknown`])

/**
 * A supplemental package that needs the seller. Vision makes one automatic
 * attempt per provider contract and never retries on its own, so an
 * unsuccessful attempt stays here until the seller acts on it.
 *
 * The server decides which actions are possible, so an offered action is reason
 * enough to show the package. Listing states alone hid an attempt stranded in
 * `claimed` by a crash: the server marks it retryable once its abandonment
 * window passes, and the seller could never see the Try again it was offering.
 */
export const needsSellerAttention = (attempt: SupplementalAttemptDto): boolean =>
  attempt.canRetrySend || ATTENTION_STATES.has(attempt.state)

export const SupplementalPackageAlerts: FC<SupplementalPackageAlertsProps> = ({
  attempts,
  onRetrySend,
  pendingAttemptId,
  actionError,
  actionErrorAttemptId,
}) => {
  const attention = attempts.filter(needsSellerAttention)

  if (attention.length === 0) {
    return null
  }

  return (
    <div className="mb-6 space-y-3">
      {attention.map((attempt) => {
        const isPending = pendingAttemptId === attempt.id

        return (
          <div
            key={attempt.id}
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            data-testid={`supplemental-alert-${attempt.id}`}
          >
            <p className="font-semibold">
              {HEADINGS[attempt.state] ??
                `Supplemental documents need attention`}
            </p>
            {attempt.failureMessage ? (
              <p className="mt-1 text-red-700">{attempt.failureMessage}</p>
            ) : null}

            <div className="mt-3 flex items-center gap-3">
              {attempt.canRetrySend ? (
                <button
                  type="button"
                  onClick={() => onRetrySend?.(attempt.id)}
                  disabled={isPending}
                  className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isPending ? `Sending…` : `Try again`}
                </button>
              ) : null}

              {/* An uncertain send offers no action: another envelope may already
                  be with the homeowner. */}
              {!attempt.canRetrySend ? (
                <span className="text-red-700">
                  Contact support to continue.
                </span>
              ) : null}
            </div>

            {!isPending && actionError && actionErrorAttemptId === attempt.id ? (
              <p className="mt-2 text-red-700">{actionError}</p>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
